/**
 * Flashcard Statistics Calculator
 *
 * Computes accuracy, readiness scores, and session summaries.
 */

import { DOMAINS } from '../../../shared/constants/domains';

/**
 * Calculate accuracy per domain.
 * Returns { domainId: { total, seen, correct, accuracy } }
 */
export function domainAccuracy(cards) {
  const stats = {};
  for (const domain of DOMAINS) {
    stats[domain.id] = { total: 0, seen: 0, correct: 0, accuracy: 0 };
  }

  for (const card of cards) {
    const d = stats[card.domain];
    if (!d) continue;
    d.total++;
    d.seen += card.stats.times_seen;
    d.correct += card.stats.times_correct;
  }

  for (const domain of DOMAINS) {
    const d = stats[domain.id];
    d.accuracy = d.seen > 0 ? d.correct / d.seen : 0;
  }

  return stats;
}

/**
 * Calculate weighted readiness score per domain.
 * Factors: accuracy × coverage (cards seen / total) × exam weight
 * Returns 0-100 overall readiness.
 */
export function readinessScore(cards) {
  const accuracy = domainAccuracy(cards);
  let totalWeightedScore = 0;

  for (const domain of DOMAINS) {
    const d = accuracy[domain.id];
    const coverage = d.total > 0 ? Math.min(1, d.seen / (d.total * 3)) : 0; // 3x seen = full coverage
    const domainScore = d.accuracy * coverage;
    totalWeightedScore += domainScore * domain.weight;
  }

  return Math.round(totalWeightedScore * 100);
}

/**
 * Find the weakest domain (lowest accuracy with minimum exposure).
 */
export function weakestDomain(cards) {
  const accuracy = domainAccuracy(cards);
  let weakest = null;
  let lowestAccuracy = 1;

  for (const domain of DOMAINS) {
    const d = accuracy[domain.id];
    if (d.seen >= 3 && d.accuracy < lowestAccuracy) { // At least 3 reviews
      lowestAccuracy = d.accuracy;
      weakest = domain;
    }
  }

  return weakest;
}

/**
 * Generate a session summary from reviewed cards and ratings.
 * @param {Array} reviews - Array of { cardId, rating, domain }
 */
export function sessionSummary(reviews) {
  const correct = reviews.filter(r => r.rating >= 2).length;
  const domainBreakdown = {};

  for (const review of reviews) {
    if (!domainBreakdown[review.domain]) {
      domainBreakdown[review.domain] = { reviewed: 0, correct: 0 };
    }
    domainBreakdown[review.domain].reviewed++;
    if (review.rating >= 2) {
      domainBreakdown[review.domain].correct++;
    }
  }

  // Calculate max streak
  let maxStreak = 0;
  let currentStreak = 0;
  for (const review of reviews) {
    if (review.rating >= 2) {
      currentStreak++;
      maxStreak = Math.max(maxStreak, currentStreak);
    } else {
      currentStreak = 0;
    }
  }

  return {
    cards_reviewed: reviews.length,
    cards_correct: correct,
    accuracy: reviews.length > 0 ? correct / reviews.length : 0,
    domain_breakdown: domainBreakdown,
    streak_max: maxStreak,
  };
}
